<template>
  <b-form-group
    label="Choose an app"
    label-align-lg="left"
  >
    <b-select
      v-model="secondaryAppId"
      data-test="select-secondary-app"
      :options="secondaryAppOptions"
      :disabled="secondaryApps.length === 0"
    />
  </b-form-group>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import {
  Tab,
  SecondaryAppReferenceNodeConfig,
} from '@/types'
import { NODE_CONFIG_KEY } from '@/constants'

const props = {
  tab: {
    type: Object as PropType<Tab>,
    required: true,
  },
}

@Component
export default class SelectSecondaryApps extends Vue.extend({ props }) {
  get secondaryApps () {
    return this.$typedStore.primary.app.allOrganisationSecondaryApps
  }

  get secondaryAppOptions () {
    return this.secondaryApps.reduce((arr, app) => {
      const notAlreadyUsedOptions = !this.secondaryAppNodeIds.find(id => id === app.id)
      const currentSelectedOption = this.secondaryAppId === app.id
      if (notAlreadyUsedOptions || currentSelectedOption) {
        const name = this.$typedStore.primary.app.secondaryAppLabel(app)
        arr.push({ value: app.id, text: name })
      }
      return arr
    }, [{ value: null, text: 'None' }] as { value: number | null, text: string }[])
  }

  get secondaryAppReferenceNodes () {
    return this.$typedStore.primary.entities.nodes.secondaryAppReferenceNodes
  }

  get secondaryAppNodeIds () {
    return this.secondaryAppReferenceNodes.reduce((arr, node) => {
      if (node.config.secondaryAppId) arr.push(node.config.secondaryAppId)
      return arr
    }, [] as number[])
  }

  get secondaryAppTabNode () {
    return this.secondaryAppReferenceNodes.find(nodes => nodes.path === this.tab.path)
  }

  get secondaryAppId () {
    return this.secondaryAppTabNode?.config.secondaryAppId ?? null
  }

  set secondaryAppId (secondaryAppId) {
    if (secondaryAppId) {
      this.loadSecondaryAppTabData(secondaryAppId)
    }

    this.updateNodeProp(secondaryAppId, 'secondaryAppId')
  }

  async loadSecondaryAppTabData (secondaryAppId: number) {
    await this.$typedStore.primary.app.getSecondaryAppConfig(secondaryAppId)
    await this.$typedStore.primary.entities.nodes.getSecondaryAppTabNodes(secondaryAppId)
    await this.$typedStore.primary.entities.images.getSecondaryAppIcons(secondaryAppId)
  }

  updateNodeProp (value: number | null, ...args: [keyof SecondaryAppReferenceNodeConfig] | []) {
    this.$typedStore.primary.entities.nodes.updateNodeProp({
      path: this.tab.path,
      configPath: [NODE_CONFIG_KEY, ...args],
      value,
    })
  }
}

</script>
